import React, { memo } from 'react';
import useCars from 'app/hook/cars/useCars';
import { numberWithCommas } from 'app/libs/number';
import { getCarYear } from 'app/libs/car';
import useService from 'app/hook/cars/useService';
import dayjs from 'app/libs/dayjs';

import TooltipPopper from 'app/components/common/TooltipPopper';

import classNames from 'classnames/bind';
import styles from './DetailSpec.module.scss';

const cx = classNames.bind(styles);

function DetailSpec() {
	const { base } = useCars();
	const { isService } = useService();
	const { category, spec, contact, manage, condition } = base;

	const colorName = spec.colorName || '-';
	const customColor = spec.customColor ? ` (${spec.customColor})` : '';
	const carColorName = `${colorName}${customColor}`;

	return (
		<>
			<strong className="blind">기본정보</strong>
			{base.vehicleType === 'CAR' && (
				<ul className={cx('list_default')}>
					<li>
						<strong className={cx('tit')}>차량번호</strong>
						<span className={cx('txt')}>{base.vehicleNo}</span>
					</li>
					<li>
						<strong className={cx('tit')}>연식</strong>
						<span className={cx('txt')}>{getCarYear(category.yearMonth, category.formYear, 'case2')}</span>
					</li>
					<li>
						<strong className={cx('tit')}>주행거리</strong>
						<span className={cx('txt')}>{numberWithCommas(spec.mileage)}km</span>
					</li>
					<li>
						<strong className={cx('tit')}>배기량</strong>
						<span className={cx('txt')}>{numberWithCommas(spec.displacement)}cc</span>
					</li>
					<li>
						<strong className={cx('tit')}>연료</strong>
						<span className={cx('txt')}>
							<>
								{spec.fuelName || ''}
								{spec.fuelName === 'LPG(일반인 구입)' && (
									<TooltipPopper
										icon="uiico_question"
										btnStyle={{ margin: '-3px 0 0 5px' }}
										tooltip={
											<>
												<b>[LPG 차량 규제완화]</b> 2019년 3월 26일부로 모든 LPG 차량의 일반인 구입이 가능합니다.
											</>
										}
										iconText="LPG 자세히보기"
										contentClass={cx('on_layer_tooltip')}
									/>
								)}
							</>
						</span>
					</li>
					<li>
						<strong className={cx('tit')}>변속기</strong>
						<span className={cx('txt')}>{spec.transmissionName || '-'}</span>
					</li>
					<li>
						<strong className={cx('tit')}>차종</strong>
						<span className={cx('txt')}>{spec.bodyName || '-'}</span>
					</li>
					<li>
						<strong className={cx('tit')}>색상</strong>
						<span className={cx('txt')}>{carColorName}</span>
					</li>
					<li>
						<strong className={cx('tit')}>지역</strong>
						<span className={cx('txt')}>{contact.address?.split(' ')[0] || '-'}</span>
					</li>
					<li>
						<strong className={cx('tit')}>인승</strong>
						<span className={cx('txt')}>{!spec.seatCount ? '-' : `${spec.seatCount}인승`}</span>
					</li>
					{!category.domestic && (
						<>
							{category.importType !== 'NONE_IMPORT_TYPE' && (
								<li>
									<strong className={cx('tit')}>수입구분</strong>
									<span className={cx('txt')}>
										{(category.importType === 'REGULAR_IMPORT' && '정식수입') ||
											(category.importType === 'PARALLEL_IMPORT' && '병행수입')}
									</span>
								</li>
							)}
							{category.warranty?.companyName && (
								<li>
									<strong className={cx('tit')}>
										제조사보증
										<TooltipPopper
											icon="uiico_question"
											btnStyle={{ margin: '-3px 0 0 5px' }}
											tooltip="판매자가 입력한 정보입니다. 상세한 내용은 판매자에게 확인하세요."
											iconText="제조사보증 자세히보기"
											contentClass={cx('on_layer_tooltip')}
										/>
									</strong>
									<span className={cx('txt')}>{category.warranty?.companyName}</span>
								</li>
							)}
						</>
					)}
					{base.contact?.userType === 'DEALER' && (
						<li>
							<strong className={cx('tit')}>압류 &middot; 저당</strong>
							<span className={cx('txt')}>
								{condition?.seizing?.seizingCount != null ? `${condition.seizing?.seizingCount}건` : '정보없음'} &middot;{' '}
								{condition?.seizing?.pledgeCount != null ? `${condition.seizing?.pledgeCount}건` : '정보없음'}
							</span>
						</li>
					)}
					<li>
						<strong className={cx('tit')}>
							조회수
							<TooltipPopper
								icon="uiico_question"
								btnStyle={{ margin: '-3px 0 0 5px' }}
								tooltip={
									<>
										{!isService.meetgo && (
											<>
												최초등록일 {dayjs(manage.firstAdvertisedDateTime).format('YYYY/MM/DD')}
												<br />
											</>
										)}
										조회수는 10분 간격으로 반영됩니다.
									</>
								}
								iconText="조회수 자세히보기"
								contentClass={cx('on_layer_tooltip')}
							/>
						</strong>
						<span className={cx('txt')}>{manage.viewCount ? numberWithCommas(manage.viewCount) : '-'}</span>
					</li>
					<li>
						<strong className={cx('tit')}>찜</strong>
						<span className={cx('txt')}>{numberWithCommas(manage.subscribeCount)}</span>
					</li>
				</ul>
			)}
			{base.vehicleType === 'TRUCK' && (
				<ul className={cx('list_default')}>
					<li>
						<strong className={cx('tit')}>차량번호</strong>
						<span className={cx('txt')}>{base.vehicleNo}</span>
					</li>
					<li>
						<strong className={cx('tit')}>연식</strong>
						<span className={cx('txt')}>{getCarYear(category.yearMonth, category.formYear, 'case2')}</span>
					</li>
					<li>
						<strong className={cx('tit')}>주행거리</strong>
						<span className={cx('txt')}>{numberWithCommas(spec.mileage)}km</span>
					</li>
					<li>
						<strong className={cx('tit')}>배기량 &middot; 마력</strong>
						<span className={cx('txt')}>
							{numberWithCommas(spec.displacement || 0)}cc &middot; {numberWithCommas(spec.horsePower || 0)}마력
						</span>
					</li>
					<li>
						<strong className={cx('tit')}>연료</strong>
						<span className={cx('txt')}>
							<>
								{spec.fuelName || '-'}
								{spec.fuelName === 'LPG(일반인 구입)' && (
									<TooltipPopper
										icon="uiico_question"
										btnStyle={{ margin: '-3px 0 0 5px' }}
										tooltip={
											<>
												<b>[LPG 차량 규제완화]</b> 2019년 3월 26일부로 모든 LPG 차량의 일반인 구입이 가능합니다.
											</>
										}
										iconText="LPG 자세히보기"
										contentClass={cx('on_layer_tooltip')}
									/>
								)}
							</>
						</span>
					</li>
					<li>
						<strong className={cx('tit')}>변속기</strong>
						<span className={cx('txt')}>{spec.transmissionName || '-'}</span>
					</li>
					<li>
						<strong className={cx('tit')}>형식</strong>
						<span className={cx('txt')}>{category.formName || '-'}</span>
					</li>
					<li>
						<strong className={cx('tit')}>색상</strong>
						<span className={cx('txt')}>{carColorName}</span>
					</li>
					<li>
						<strong className={cx('tit')}>용도</strong>
						<span className={cx('txt')}>{spec.use || '-'}</span>
					</li>
					{category.specialManufacturerName && (
						<li>
							<strong className={cx('tit')}>특수/특장 제조사</strong>
							<span className={cx('txt')}>{category.specialManufacturerName}</span>
						</li>
					)}
					{category.capacityName && (
						<li>
							<strong className={cx('tit')}>적재용량</strong>
							<span className={cx('txt')}>{category.capacityName}</span>
						</li>
					)}
					{spec.varaxis && (
						<li>
							<strong className={cx('tit')}>가변축</strong>
							<span className={cx('txt')}>{spec.varaxis}</span>
						</li>
					)}
					{spec.carryingBox && (
						<li>
							<strong className={cx('tit')}>적재함 크기</strong>
							<span className={cx('txt')}>{`${spec.carryingBoxBore}*${spec.carryingBoxHeight}*${spec.carryingBoxWidth}`}</span>
						</li>
					)}
					{spec.capacityStandardName && (
						<li>
							<strong className={cx('tit')}>적재 규격</strong>
							<span className={cx('txt')}>{spec.capacityStandardName}</span>
						</li>
					)}
					<li>
						<strong className={cx('tit')}>지역</strong>
						<span className={cx('txt')}>{contact.address?.split(' ')[0] || '-'}</span>
					</li>
					{base.contact?.userType === 'DEALER' && (
						<li>
							<strong className={cx('tit')}>압류 &middot; 저당</strong>
							<span className={cx('txt')}>
								{condition?.seizing?.seizingCount != null ? condition.seizing?.seizingCount : '정보없음'} &middot;{' '}
								{condition?.seizing?.pledgeCount != null ? condition.seizing?.pledgeCount : '정보없음'}
							</span>
						</li>
					)}
					<li>
						<strong className={cx('tit')}>
							조회수
							<TooltipPopper
								icon="uiico_question"
								btnStyle={{ margin: '-3px 0 0 5px' }}
								tooltip={
									<>
										{!isService.meetgo && (
											<>
												최초등록일 {dayjs(manage.firstAdvertisedDateTime).format('YYYY/MM/DD')}
												<br />
											</>
										)}
										조회수는 10분 간격으로 반영됩니다.
									</>
								}
								iconText="조회수 자세히보기"
								contentClass={cx('on_layer_tooltip')}
							/>
						</strong>
						<span className={cx('txt')}>{manage.viewCount ? numberWithCommas(manage.viewCount) : '-'}</span>
					</li>
					<li>
						<strong className={cx('tit')}>찜</strong>
						<span className={cx('txt')}>{numberWithCommas(manage.subscribeCount)}</span>
					</li>
				</ul>
			)}
		</>
	);
}

export default memo(DetailSpec);
